import { useState } from "react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Sparkles, Save, Loader2 } from "lucide-react";
import api from "../lib/api";

const LONG_FIELDS = ["endereco", "endereco_empresa", "segunda_reclamada", "jornada_real", "jornada_contratual",
  "descontos_indevidos", "insalubridade_detalhes", "periculosidade_detalhes", "danos_morais",
  "danos_morais_detalhes", "acidente_trabalho", "observacoes_gerais", "dias_trabalhados"];

export default function DataReview({ caseData, fieldGroups, onSaved }) {
  const [data, setData] = useState(caseData.extracted_data || {});
  const [extractedFields, setExtractedFields] = useState(new Set(caseData.extracted_fields || []));
  const [saving, setSaving] = useState(false);

  const setField = (key, value) => {
    setData((d) => ({ ...d, [key]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/cases/${caseData.id}`, {
        extracted_data: data,
        extracted_fields: Array.from(extractedFields),
        status: caseData.status === "novo" ? "revisao" : caseData.status,
      });
      toast.success("Dados salvos");
      onSaved({ ...caseData, extracted_data: data });
    } catch (e) {
      toast.error("Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const hasExtraction = (caseData.extracted_fields || []).length > 0;

  return (
    <div className="space-y-6">
      {!hasExtraction && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
          Ainda não há dados extraídos. Vá para a aba <strong>Documentos</strong> e execute a extração — ou preencha manualmente abaixo.
        </div>
      )}
      <div className="flex items-center justify-between sticky top-0 bg-background/95 backdrop-blur py-2 z-10">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <span className="h-2 w-2 rounded-full bg-emerald-500" /> Extraído pela IA — revisar
          </span>
        </div>
        <Button data-testid="save-review-btn" onClick={save} disabled={saving}
          className="bg-slate-900 hover:bg-slate-800 text-white gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar revisão
        </Button>
      </div>

      {fieldGroups.map((group) => (
        <div key={group.id} className="bg-white border rounded-xl p-5" data-testid={`field-group-${group.id}`}>
          <h3 className="font-display font-bold text-sm mb-4 pb-2 border-b">{group.title}</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {group.fields.map((f) => {
              const isLong = LONG_FIELDS.includes(f.key);
              const isExtracted = extractedFields.has(f.key) && (data[f.key] || "").length > 0;
              return (
                <div key={f.key} className={isLong ? "sm:col-span-2" : ""}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <label className="text-sm font-medium">{f.label}</label>
                    {isExtracted && (
                      <Badge data-testid={`ai-badge-${f.key}`}
                        className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] gap-1 py-0 h-5 hover:bg-emerald-50">
                        <Sparkles className="h-2.5 w-2.5" /> extraído, revisar
                      </Badge>
                    )}
                  </div>
                  {isLong ? (
                    <Textarea data-testid={`field-${f.key}`} value={data[f.key] || ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      className={`bg-white ${isExtracted ? "border-emerald-300 bg-emerald-50/30" : ""}`} rows={2} />
                  ) : (
                    <Input data-testid={`field-${f.key}`} value={data[f.key] || ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      className={`bg-white ${isExtracted ? "border-emerald-300 bg-emerald-50/30" : ""}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
